import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ── Auto-refresh on 401 ──────────────────────────────────────────────────────
// When the short-lived access_token expires, the next API call returns 401
// with detail "Não autenticado" / "Token inválido". Instead of bouncing the
// user to /login, we try ONCE to refresh the access token using the long-lived
// refresh_token cookie, then transparently replay the original request.
//
// If the refresh itself fails (refresh_token also expired/missing), THEN we
// redirect to /login. This dramatically reduces accidental logouts on
// long-running admin sessions (team management, audit, dashboards).
let isRefreshing = false;
let refreshQueue = [];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only retry on 401 AND only once per request
    if (status !== 401 || original?._retry) return Promise.reject(error);
    // Never retry the refresh call itself nor /auth/me — the latter is
    // intentionally allowed to fail for anonymous visitors (Landing, public
    // questionnaire, etc). Triggering a refresh + redirect on /auth/me's 401
    // would kick guests off the public homepage to /login.
    if (
      original.url?.includes("/auth/refresh") ||
      original.url?.includes("/auth/login") ||
      original.url?.includes("/auth/me")
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    if (isRefreshing) {
      // Queue this request until the in-flight refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: () => resolve(api(original)),
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      await api.post("/auth/refresh", null, { _retry: true });
      // Flush queued requests
      refreshQueue.forEach((p) => p.resolve());
      refreshQueue = [];
      return api(original);
    } catch (refreshErr) {
      refreshQueue.forEach((p) => p.reject(refreshErr));
      refreshQueue = [];
      // Refresh failed → user truly needs to re-login
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?next=${next}`;
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
